import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import userService from "@/api/services/userService";
import menuService from "@/api/services/menuService";

import { GLOBAL_CONFIG } from "@/global-config";
import { encryption } from "@/utils/cryption";
import { getItem, removeItem, setItem } from "@/utils/storage";
import to from "await-to-js";
import { toast } from "sonner";
import { StorageEnum } from "#/enum";
import {
	WechatAuthRedirectPath,
	WechatAuthState,
	WechatAuthUri,
	LoginExtraParams,
} from "./consts";
import type {
	WechatLogin,
	GetUserInfo,
	Login,
	Logout,
	UserLoginFormData,
} from "./userDomain";
import type { UserInfo } from "./userEntity";
import type { PageListReq } from "@/types/api";

export const useUsers = (listReq: PageListReq<{ username?: string }>) => {
	return useQuery({
		queryKey: ["user-list", ...Object.values(listReq)],
		queryFn: () =>
			userService.getUserPage({
				current: listReq.current,
				size: listReq.pageSize,
				username: listReq.username || undefined,
			}),
		staleTime: 1000 * 60 * 5, // 5 minutes
	});
};
export const useUser = () => {
	return useQuery({
		queryKey: ["user"],
		queryFn: (async () => {
			const res = await userService.getUserInfo();
			const menus = await menuService.getMenus();
			const { sysUser, permissions, roles } = res;
			const userInfo: UserInfo = {
				...sysUser,
				id: sysUser.userId.toString(),
				email: sysUser.email,
				username: sysUser.username,
				avatar: sysUser.avatar,
				password: sysUser.password,
				user: sysUser,
				permissions,
				roles,
				menus,
			};
			return userInfo;
		}) satisfies GetUserInfo,
		enabled: !!getItem(StorageEnum.UserToken),
		staleTime: 1000 * 60 * 5, // 5 minutes
	});
};

export const useSignIn = () => {
	const queryClient = useQueryClient();
	const signInMutation = useMutation({
		mutationFn: async (payload: UserLoginFormData) => {
			const { access_token, exp, refresh_token, refresh_token_exp } =
				await userService.signin({
					params: {
						username: payload.username,
						...LoginExtraParams,
					},
					data: {
						password: encryption(
							payload.password,
							GLOBAL_CONFIG.passwordEncKey,
						),
					},
				});
			if (access_token) {
				setItem(StorageEnum.UserToken, access_token);
				setItem(StorageEnum.TokenExpire, exp);
				setItem(StorageEnum.RefreshToken, refresh_token);
				setItem(StorageEnum.RefreshTokenExpire, refresh_token_exp);
			} else {
				return Promise.reject(new Error("登录失败"));
			}
		},
		onSuccess: () => {
			// 登录成功后强制刷新用户信息
			queryClient.invalidateQueries({ queryKey: ["user"] });
		},
	});

	return signInMutation.mutateAsync satisfies Login;
};

export const useLogout = () => {
	const queryClient = useQueryClient();

	const logoutMutation = useMutation({
		mutationFn: async () => {
			removeItem(StorageEnum.UserToken);
			removeItem(StorageEnum.TokenExpire);
			removeItem(StorageEnum.RefreshToken);
			removeItem(StorageEnum.RefreshTokenExpire);
			queryClient.invalidateQueries({ queryKey: ["user"] });
		},
		onSuccess: () => {
			toast.success("登出成功", {
				position: "top-center",
			});
		},
	});
	const logout = async () => {
		const [logoutError] = await to(logoutMutation.mutateAsync());
		if (logoutError) {
			toast.error("登出失败", {
				position: "top-center",
			});
			throw logoutError;
		}
	};
	return logout satisfies Logout;
};

export const useWechatSignIn = () => {
	const wechatSigninMutation = useMutation({
		mutationFn: async () => {
			const appList = await userService.getLoginAppList();
			const { appId } = appList.find((app) => app.type === "WX") || {};
			const redirectUri = encodeURIComponent(
				`${window.location.origin}${WechatAuthRedirectPath}`,
			);
			const url = `${WechatAuthUri}?appid=${appId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_login&state=${WechatAuthState}#wechat_redirect`;
			window.location.href = url;
		},
	});
	const wechatSignIn = async () => {
		const [wechatError] = await to(wechatSigninMutation.mutateAsync());
		if (wechatError) {
			toast.error("微信登录失败", {
				position: "top-center",
			});
			throw wechatError;
		}
	};
	return wechatSignIn satisfies WechatLogin;
};

export type SmsLoginFormData = {
	mobile: string;
	code: string;
};

export const useSmsSignIn = () => {
	const queryClient = useQueryClient();
	const smsSignInMutation = useMutation({
		mutationFn: async (payload: SmsLoginFormData) => {
			const { access_token, exp, refresh_token, refresh_token_exp } =
				await userService.signinWithSms(payload.mobile, payload.code);
			if (access_token) {
				setItem(StorageEnum.UserToken, access_token);
				setItem(StorageEnum.TokenExpire, exp);
				setItem(StorageEnum.RefreshToken, refresh_token);
				setItem(StorageEnum.RefreshTokenExpire, refresh_token_exp);
			} else {
				return Promise.reject(new Error("登录失败"));
			}
		},
		onSuccess: () => {
			// 登录成功后强制刷新用户信息
			queryClient.invalidateQueries({ queryKey: ["user"] });
		},
	});

	return smsSignInMutation.mutateAsync;
};
